/**
 * Visual Editor — unified tabbed workspace for the Style's visual data
 * (defect pins, BOM hotspots, measurements, photos, design markings).
 *
 * Supports deep-linking via `?tab=design|bom|measure|photo` (defect is default).
 * The `/design` route redirects here with `?tab=design`.
 *
 * @see prototype/garment/visual-style-file.html — the canvas baseline
 * @see redesign-phases.md — Phase 1
 */
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { WorkspaceShell, requireModule } from "../../../../features/workspace/index.ts";
import { VisualEditorPage } from "../../../../features/styles/index.ts";

export const Route = createFileRoute("/(workspace)/merchandising/styles/$styleId/visual")({
  // The unified visual editor (defect/BOM/measure/photo/design) is the visual
  // CAD surface — part of the 1.0-withheld Design Studio sub-gate, withheld
  // even in a garment workspace. The /design route redirects here, so this is
  // the single gate for the whole surface.
  // @see docs/adr/0046-withheld-1.0-modules.md
  beforeLoad: requireModule("design-studio"),
  validateSearch: z.object({
    tab: z.enum(["defect", "bom", "measure", "photo", "design"]).optional(),
  }),
  component: VisualStyleFileRoute,
});

function VisualStyleFileRoute() {
  const { styleId } = Route.useParams();
  const { tab } = Route.useSearch();
  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Merchandising", to: "/" },
        { label: "Style files", to: "/merchandising/styles" },
        { label: "Visual" },
      ]}
      contentClassName="max-w-none"
      viewportClassName="p-0 md:p-0"
    >
      <VisualEditorPage styleId={styleId} initialTab={tab} />
    </WorkspaceShell>
  );
}
